import { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Plus, Trash, Calendar, TrendUp, PiggyBank, Wallet } from '@phosphor-icons/react';
import { toast } from 'sonner';
import LoadingBar from '../components/LoadingBar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Goals = () => {
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saldoLivreMensal, setSaldoLivreMensal] = useState(0);
  const [newGoal, setNewGoal] = useState({ 
    name: '', 
    target_amount: '', 
    current_amount: '0', 
    deadline: '', 
    category: 'Outros'
  });
  
  // State for add funds dialog
  const [addFundsDialog, setAddFundsDialog] = useState(false);
  const [pendingGoalUpdate, setPendingGoalUpdate] = useState(null);
  const [pendingAmount, setPendingAmount] = useState(0);

  useEffect(() => {
    fetchGoals();
  }, []);

  const fetchGoals = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/goals/with-forecast`, { withCredentials: true });
      setGoals(res.data.goals);
      setSaldoLivreMensal(res.data.saldo_livre_mensal);
    } catch (error) {
      toast.error('Erro ao carregar metas');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    if (!newGoal.deadline) {
      toast.error('Por favor, defina uma data limite para a meta');
      return;
    }
    
    try {
      await axios.post(`${BACKEND_URL}/api/goals`, {
        ...newGoal,
        target_amount: parseFloat(newGoal.target_amount),
        current_amount: parseFloat(newGoal.current_amount || 0)
      }, { withCredentials: true });
      toast.success('Meta criada!');
      setOpen(false);
      fetchGoals();
      setNewGoal({ name: '', target_amount: '', current_amount: '0', deadline: '', category: 'Outros' });
    } catch (error) {
      toast.error('Erro ao criar meta');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/goals/${id}`, { withCredentials: true });
      toast.success('Meta excluída');
      fetchGoals();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  // New function to handle add funds with expense option
  const initiateAddFunds = (goal, amount) => {
    if (!amount || amount <= 0) {
      toast.error('Digite um valor válido');
      return;
    }
    setPendingGoalUpdate(goal);
    setPendingAmount(parseFloat(amount));
    setAddFundsDialog(true);
  };

  const confirmAddFunds = async (registerAsExpense) => {
    if (!pendingGoalUpdate || !pendingAmount) return;

    try {
      const newTotal = pendingGoalUpdate.current_amount + pendingAmount;
      
      // Update goal progress
      await axios.patch(`${BACKEND_URL}/api/goals/${pendingGoalUpdate.goal_id}`, { 
        current_amount: newTotal 
      }, { withCredentials: true });
      
      // If user wants to register as expense
      if (registerAsExpense) {
        await axios.post(`${BACKEND_URL}/api/transactions`, {
          type: 'expense',
          amount: pendingAmount,
          description: `Meta: ${pendingGoalUpdate.name}`,
          category: 'investimentos',
          date: new Date().toISOString(),
          recurrence: 'pontual'
        }, { withCredentials: true });
        
        toast.success(`R$ ${pendingAmount.toFixed(2)} adicionado à meta e registrado como despesa!`);
      } else {
        toast.success(`R$ ${pendingAmount.toFixed(2)} adicionado à meta (valor de outra fonte)`);
      }
      
      fetchGoals();
    } catch (error) {
      toast.error('Erro ao atualizar meta');
    } finally {
      setAddFundsDialog(false);
      setPendingGoalUpdate(null);
      setPendingAmount(0);
    }
  };

  if (loading) return <LoadingBar text="Carregando metas..." />;

  return (
    <div className="p-3 sm:p-6 md:p-8 space-y-6" data-testid="goals-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight mb-1 sm:mb-2 text-gradient-green">Metas</h1>
          <p className="text-sm sm:text-base text-[var(--neo-text-light)]">
            Defina e acompanhe seus objetivos financeiros • Saldo livre mensal: 
            <span className={`font-bold ml-1 ${saldoLivreMensal >= 0 ? 'text-[var(--neo-green)]' : 'text-[var(--neo-red)]'}`}>
              R$ {saldoLivreMensal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </span>
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button className="neo-btn flex items-center" data-testid="add-goal-button">
              <Plus size={20} className="mr-2" />Nova Meta
            </button>
          </DialogTrigger>
          <DialogContent className="neo-card border-none">
            <DialogHeader>
              <DialogTitle className="text-[var(--neo-text-dark)]">Nova Meta</DialogTitle>
              <DialogDescription className="text-[var(--neo-text-light)]">Defina um objetivo financeiro com prazo</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <Label className="text-[var(--neo-text)]">Nome da Meta</Label>
                <input 
                  value={newGoal.name} 
                  onChange={(e) => setNewGoal({...newGoal, name: e.target.value})} 
                  data-testid="goal-name" 
                  placeholder="Ex: Viagem para Europa" 
                  required 
                  className="neo-input mt-1"
                />
              </div>
              <div>
                <Label className="text-[var(--neo-text)]">Tipo</Label>
                <Select value={newGoal.category} onValueChange={(val) => setNewGoal({...newGoal, category: val})}>
                  <SelectTrigger data-testid="goal-category-select" className="neo-input mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Viagem">Viagem</SelectItem>
                    <SelectItem value="Compra">Compra</SelectItem>
                    <SelectItem value="Emergência">Emergência</SelectItem>
                    <SelectItem value="Investimento">Investimento</SelectItem>
                    <SelectItem value="Outros">Outros</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[var(--neo-text)]">Valor Total</Label>
                <input 
                  type="number" 
                  step="0.01" 
                  value={newGoal.target_amount} 
                  onChange={(e) => setNewGoal({...newGoal, target_amount: e.target.value})} 
                  data-testid="goal-target" 
                  placeholder="0.00"
                  required 
                  className="neo-input mt-1"
                />
              </div>
              <div>
                <Label className="text-[var(--neo-text)]">Valor Já Guardado (opcional)</Label>
                <input 
                  type="number" 
                  step="0.01" 
                  value={newGoal.current_amount} 
                  onChange={(e) => setNewGoal({...newGoal, current_amount: e.target.value})} 
                  data-testid="goal-current" 
                  placeholder="0.00" 
                  className="neo-input mt-1"
                />
              </div>
              <div>
                <Label className="text-[var(--neo-text)]">Data Limite</Label>
                <input 
                  type="date" 
                  value={newGoal.deadline} 
                  onChange={(e) => setNewGoal({...newGoal, deadline: e.target.value})} 
                  data-testid="goal-deadline" 
                  required
                  className="neo-input mt-1"
                />
              </div>
              <button type="submit" className="neo-btn w-full" data-testid="submit-goal">Criar Meta</button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {goals.length === 0 ? (
          <div className="col-span-full neo-card text-center">
            <PiggyBank size={48} className="mx-auto mb-4 text-[var(--neo-text-light)] opacity-50" />
            <p className="text-[var(--neo-text-light)]">Nenhuma meta definida ainda</p>
            <p className="text-sm text-[var(--neo-text-light)] mt-2">Crie sua primeira meta clicando no botão acima!</p>
          </div>
        ) : (
          goals.map(goal => {
            const progress = (goal.current_amount / goal.target_amount) * 100;
            const statusColors = {
              achieved: 'neo-badge-green',
              on_track: 'bg-blue-100 text-blue-700',
              at_risk: 'bg-[var(--neo-gold)]/20 text-[var(--neo-gold-dark)]',
              insufficient_income: 'bg-[var(--neo-red)]/10 text-[var(--neo-red)]',
              pending: 'bg-[var(--neo-bg)] text-[var(--neo-text-light)]'
            };
            
            const statusLabels = {
              achieved: '✓ Meta Alcançada',
              on_track: '✓ No Caminho Certo',
              at_risk: '⚠ Risco de Atraso',
              insufficient_income: '✗ Renda Insuficiente',
              pending: 'Pendente'
            };
            
            return (
              <div key={goal.goal_id} className="neo-card" data-testid={`goal-${goal.goal_id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-[var(--neo-text-dark)] mb-1">{goal.name}</h3>
                    <p className="text-sm text-[var(--neo-text-light)]">{goal.category}</p>
                    <div className={`inline-block mt-2 px-3 py-1 rounded-full text-xs font-medium ${statusColors[goal.status]}`}>
                      {statusLabels[goal.status]}
                    </div>
                  </div>
                  <button 
                    className="neo-icon-btn w-10 h-10"
                    onClick={() => handleDelete(goal.goal_id)} 
                    data-testid={`delete-goal-${goal.goal_id}`}
                  >
                    <Trash size={18} className="text-[var(--neo-red)]" />
                  </button>
                </div>

                {/* Progress Bar */}
                <div className="mb-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-[var(--neo-text-light)]">Progresso</span>
                    <span className="font-semibold text-[var(--neo-green)]">{(isNaN(progress) ? 0 : progress).toFixed(1)}%</span>
                  </div>
                  <div className="neo-progress">
                    <div 
                      className="neo-progress-bar" 
                      style={{ width: `${Math.min(progress, 100)}%` }}
                    />
                  </div>
                </div>

                {/* Current vs Target */}
                <div className="flex justify-between items-center py-4 border-t border-[var(--neo-shadow-dark)]/30">
                  <div>
                    <p className="text-xs text-[var(--neo-text-light)]">Guardado</p>
                    <p className="text-xl font-bold tabular-nums text-[var(--neo-green)]">
                      R$ {goal.current_amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-[var(--neo-text-light)]">Meta</p>
                    <p className="text-xl font-bold tabular-nums text-[var(--neo-text-dark)]">
                      R$ {goal.target_amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>

                {/* Forecast */}
                {goal.can_achieve && goal.months_to_achieve !== null && goal.status !== 'achieved' && (
                  <div className="neo-pressed p-4 rounded-xl mb-4">
                    <div className="flex items-center text-sm">
                      <TrendUp size={20} className="mr-3 text-[var(--neo-green)]" weight="bold" />
                      <div>
                        <p className="font-semibold text-[var(--neo-green)]">
                          Meta atingida em até {goal.months_to_achieve} {goal.months_to_achieve === 1 ? 'mês' : 'meses'}
                        </p>
                        <p className="text-xs text-[var(--neo-text-light)] mt-1">
                          Com seu saldo livre mensal de R$ {(saldoLivreMensal ?? 0).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {goal.status === 'insufficient_income' && (
                  <div className="neo-pressed p-4 rounded-xl mb-4 border-l-4 border-[var(--neo-red)]">
                    <p className="text-sm text-[var(--neo-red)]">
                      ⚠ Seu saldo livre mensal é insuficiente para esta meta. 
                      Aumente suas receitas ou reduza despesas.
                    </p>
                  </div>
                )}

                {/* Deadline */}
                {goal.deadline && (
                  <div className="flex items-center text-sm text-[var(--neo-text-light)] py-3 border-t border-[var(--neo-shadow-dark)]/30">
                    <Calendar size={16} className="mr-2" />
                    <span>Prazo: {new Date(goal.deadline).toLocaleDateString('pt-BR')}</span>
                  </div>
                )}

                {/* Add Funds */}
                {goal.status !== 'achieved' && (
                  <div className="pt-3 border-t border-[var(--neo-shadow-dark)]/30">
                    <p className="text-xs text-[var(--neo-text-light)] mb-2">Adicionar valor à meta:</p>
                    <div className="flex space-x-2">
                      <input 
                        type="number" 
                        step="0.01" 
                        placeholder="Valor" 
                        data-testid={`update-goal-${goal.goal_id}`}
                        id={`input-goal-${goal.goal_id}`}
                        className="neo-input flex-1"
                      />
                      <button 
                        className="neo-btn px-4"
                        onClick={() => {
                          const input = document.getElementById(`input-goal-${goal.goal_id}`);
                          if (input && input.value) {
                            initiateAddFunds(goal, input.value);
                            input.value = '';
                          }
                        }}
                      >
                        <Plus size={18} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Add Funds Confirmation Dialog */}
      <Dialog open={addFundsDialog} onOpenChange={setAddFundsDialog}>
        <DialogContent className="neo-card border-none max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[var(--neo-text-dark)] flex items-center gap-2">
              <PiggyBank size={24} className="text-[var(--neo-green)]" />
              Adicionar à Meta
            </DialogTitle>
            <DialogDescription className="text-[var(--neo-text-light)]">
              Você está adicionando <span className="font-bold text-[var(--neo-green)]">R$ {pendingAmount.toFixed(2)}</span> à meta "{pendingGoalUpdate?.name}"
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-[var(--neo-text)] mb-4 text-center">
              De onde vem esse dinheiro?
            </p>
            
            <div className="space-y-3">
              <button
                onClick={() => confirmAddFunds(true)}
                className="w-full neo-raised p-4 rounded-xl flex items-center gap-4 hover:scale-[1.02] transition-all border-2 border-transparent hover:border-[var(--neo-green)]"
              >
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'var(--neo-green)', color: 'white' }}>
                  <Wallet size={24} weight="bold" />
                </div>
                <div className="text-left flex-1">
                  <p className="font-semibold text-[var(--neo-text-dark)]">Do meu saldo atual</p>
                  <p className="text-sm text-[var(--neo-text-light)]">Registrar como despesa (saiu do saldo)</p>
                </div>
              </button>
              
              <button
                onClick={() => confirmAddFunds(false)}
                className="w-full neo-raised p-4 rounded-xl flex items-center gap-4 hover:scale-[1.02] transition-all border-2 border-transparent hover:border-[var(--neo-gold)]"
              >
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'var(--neo-gold)', color: 'white' }}>
                  <PiggyBank size={24} weight="bold" />
                </div>
                <div className="text-left flex-1">
                  <p className="font-semibold text-[var(--neo-text-dark)]">De outra fonte</p>
                  <p className="text-sm text-[var(--neo-text-light)]">Dinheiro extra, presente, etc. (não afeta o saldo)</p>
                </div>
              </button>
            </div>
          </div>
          
          <DialogFooter>
            <button 
              onClick={() => setAddFundsDialog(false)}
              className="neo-btn-outline w-full"
            >
              Cancelar
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Goals;
